import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  makeStyles,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { Coordinate, Direction, SpotlightProps } from '../Spotlight';
import { setStorageItem, STORAGE_ACTION } from '@utils/storage';
import { useHistory } from 'react-router-dom';
import useUserActionLog, {
  CapaActivityType,
  Enum_Action_Button_Enum
} from '@hooks/useUserActionLog';

interface SpotlightCTAProps
  extends Pick<
    SpotlightProps,
    | 'CTADirection'
    | 'CTATitle'
    | 'CTADescription'
    | 'addX'
    | 'addY'
    | 'handleNextStep'
  > {
  coordinate: Coordinate;
  handleClose: () => void;
}

const SpotlightCTA = ({
  coordinate,
  CTADirection,
  CTATitle,
  CTADescription,
  addX = 0,
  addY = 0,
  handleClose,
  handleNextStep
}: SpotlightCTAProps) => {
  const { onInsertUserActionLogOne } = useUserActionLog();
  const matches = useMediaQuery(useTheme().breakpoints.down('md'));
  const [check, setCheck] = useState(false);
  const { spotlightCTACheckBox, spotlightCTAColor } = useStyles();
  const history = useHistory();
  const location = history.location;
  const { x, y } = coordinate;

  const width = matches ? 225 : 272;
  const height = matches ? 198 : 216;

  const { top, left } = getTopLeft({
    CTADirection,
    width,
    height,
    x,
    y,
    addX,
    addY
  });

  const handleClickNextTime = () => {
    onInsertUserActionLogOne(
      Enum_Action_Button_Enum.Button_Capa_Viewer_Tutorial_See_Next_Time,
      CapaActivityType.ButtonClick
    );
    if (check) {
      setStorageItem(
        STORAGE_ACTION.NOT_SEEN_AGAIN_CAPA_VIEWER_TUTORIAL,
        'true'
      );
    }

    if (location.pathname.includes('/capaviewtutorial')) {
      history.push(
        `/chat/${(location.state as { rfqId?: string })?.rfqId ?? ''}`,
        { haveBeenTutorial: true }
      );
      return;
    }
    handleClose();
  };

  return (
    <Box
      position={'absolute'}
      zIndex={10001}
      width={width}
      height={height}
      top={top}
      left={left}
    >
      <Box marginBottom={'12px'}>
        <CustomizedTypography variant="h4" fontWeight="500" color={'primary'}>
          {CTATitle}
        </CustomizedTypography>
      </Box>

      <Box color={'white'} marginBottom={'32px'}>
        <CustomizedTypography variant="body1" color="inherit">
          {CTADescription}
        </CustomizedTypography>
      </Box>

      <Box color={'white'} marginBottom={'16px'} display={'flex'} gridGap={8}>
        <CustomizedButton
          fullWidth
          variant={'outlined'}
          color={'inherit'}
          size={'small'}
          onClick={handleClickNextTime}
        >
          <CustomizedTypography
            variant="body2"
            color="inherit"
            fontWeight="500"
          >
            다음에 보기
          </CustomizedTypography>
        </CustomizedButton>
        <CustomizedButton
          fullWidth
          data-cy="spotlight-next-button"
          variant={'contained'}
          size={'small'}
          onClick={handleNextStep}
        >
          <CustomizedTypography
            variant="body2"
            color="inherit"
            fontWeight="500"
          >
            다음 단계
          </CustomizedTypography>
        </CustomizedButton>
      </Box>

      <Box
        onClick={() => {
          onInsertUserActionLogOne(
            Enum_Action_Button_Enum.Button_Capa_Viewer_Tutorial_Not_See_Again,
            CapaActivityType.ButtonClick
          );
          setCheck((state) => !state);
        }}
        display={'flex'}
        gridGap={4}
      >
        <CustomizedButton variant={'text'} size={'small'}>
          <Checkbox
            className={spotlightCTACheckBox}
            disableRipple={true}
            checked={check}
            size={'small'}
          />

          <CustomizedTypography
            className={spotlightCTAColor}
            variant="body2"
            fontWeight="500"
          >
            다시 보지 않기
          </CustomizedTypography>
        </CustomizedButton>
      </Box>
    </Box>
  );
};

export default SpotlightCTA;

const getTopLeft = ({
  CTADirection,
  width,
  height,
  x,
  y,
  addX,
  addY
}: {
  CTADirection: Direction;
  width: number;
  height: number;
  x: number | string;
  y: number | string;
  addX: number;
  addY: number;
}) => {
  if (typeof x !== 'string') {
    x = `${x}px`;
  }
  if (typeof y !== 'string') {
    y = `${y}px`;
  }

  let top, left;

  switch (CTADirection) {
    case 'left': {
      top = `calc(${y} - ${height / 2}px + ${addY}px)`;
      left = `calc(${x} - ${width / 2}px - ${width}px + ${addX}px)`;
      break;
    }
    case 'right': {
      top = `calc(${y} - ${height / 2}px + ${addY}px)`;
      left = `calc(${x} - ${width / 2}px + ${width}px + ${addX}px)`;
      break;
    }
    case 'bottom': {
      top = `calc(${y} - ${height / 2}px + ${height}px + ${addY}px)`;
      left = `calc(${x} - ${width / 2}px + ${addX}px)`;
      break;
    }
    case 'top': {
      top = `calc(${y} - ${height / 2}px - ${height}px + ${addY}px)`;
      left = `calc(${x} - ${width / 2}px + ${addX}px)`;
      break;
    }
  }
  return { top, left };
};

const useStyles = makeStyles(() => ({
  spotlightCTACheckBox: {
    padding: 0,
    marginRight: 4
  },
  spotlightCTAColor: {
    color: '#fff'
  }
}));
